import {resolvePirEeis} from './pirPayload';
import {
    arCoverageStatus,
    icrCoverageStatusFromChildren,
    resolveRequirementCoverageStatus,
} from './coverage/analyticCoverageStatus';
import {resolveAnalyticHeadlinePct} from './coverage/coverageHeadlines';

function normText(s) {
    return String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Per-answer fit band: hide below 10, partial 10–90, full ≥90. */
export function answerFitFromScore(score) {
    const n = Number(score);
    if (!Number.isFinite(n) || n < 10) return 'none';
    if (n >= 90) return 'full';
    return 'partial';
}

export function answerFitLabel(fit) {
    if (fit === 'full') return 'Fully Answers';
    if (fit === 'partial') return 'Partially Answers';
    return null;
}

/**
 * Keep at most `maxSentences` sentences (default 3).
 */
export function shortenToSentences(text, maxSentences = 3) {
    const raw = String(text || '').replace(/\s+/g, ' ').trim();
    if (!raw) return '';
    const parts = raw.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [raw];
    const sentences = parts.map((s) => s.trim()).filter(Boolean);
    if (sentences.length <= maxSentences) {
        return sentences.join(' ').trim();
    }
    return sentences.slice(0, maxSentences).join(' ').trim();
}

/** Single-phrase coverage rationale (what is covered / still missing). */
export function toCoverageRationalePhrase(text) {
    return shortenToSentences(text, 1);
}

function normalizeArView(ar) {
    if (!ar || typeof ar !== 'object') return null;
    const coveragePct = ar.coveragePct != null ? Number(ar.coveragePct) : null;
    return {
        id: ar.id != null ? String(ar.id) : null,
        text: ar.text || '',
        coveragePct: Number.isFinite(coveragePct) ? coveragePct : null,
        status: arCoverageStatus(coveragePct),
        explanation: ar.explanation || '',
        excerpts: Array.isArray(ar.excerpts) ? ar.excerpts : [],
    };
}

const SYNTHETIC_ALIGNMENT_TEXT = /^factual\s+\d+%\s*\/\s*opinion\s+\d+%$/i;

function displayExcerptText(text) {
    const trimmed = String(text || '').trim();
    if (!trimmed || SYNTHETIC_ALIGNMENT_TEXT.test(trimmed)) return '';
    return trimmed;
}

function sortFintels(a, b) {
    if (Boolean(b.linked) !== Boolean(a.linked)) return a.linked ? -1 : 1;
    return (Number(b.qualityScore) || 0) - (Number(a.qualityScore) || 0);
}

/**
 * Per-FINTEL answers for one C1 question: excerpts grouped by report, with a
 * quality score from the ARs the report contributes to (weighted by AR weight —
 * weights are internal to the computation and never rendered).
 */
function fintelsFromC1Question(question, linkedIds = new Set()) {
    if (!question) return [];
    const ars = question.ars || [];
    const singleAr = ars.length <= 1;
    const byReport = new Map();

    const addExcerpt = (excerpt, arPct, arWeight) => {
        const reportId = excerpt?.reportId;
        if (!reportId) return;
        const key = String(reportId);
        if (!byReport.has(key)) {
            byReport.set(key, {
                reportId: key,
                reportTitle: excerpt.reportTitle || excerpt.title || key,
                texts: [],
                weightSum: 0,
                weightedPct: 0,
                maxPct: null,
                linked: excerpt.linked === true || linkedIds.has(key),
            });
        }
        const entry = byReport.get(key);
        if (excerpt.reportTitle && entry.reportTitle === key) {
            entry.reportTitle = excerpt.reportTitle;
        }
        if (excerpt.linked === true) entry.linked = true;
        const text = displayExcerptText(excerpt?.text || excerpt?.conclusion);
        if (text && !entry.texts.includes(text)) entry.texts.push(text);
        const weight = Number(arWeight);
        const pct = Number(arPct);
        if (Number.isFinite(pct)) {
            entry.maxPct = entry.maxPct == null ? pct : Math.max(entry.maxPct, pct);
        }
        const usableWeight = Number.isFinite(weight) && weight > 0 ? weight : 1;
        if (Number.isFinite(pct)) {
            entry.weightSum += usableWeight;
            entry.weightedPct += usableWeight * pct;
        }
    };

    for (const ar of ars) {
        for (const excerpt of ar?.excerpts || []) {
            addExcerpt(excerpt, ar.coveragePct, ar.weight);
        }
    }
    for (const excerpt of question.excerpts || []) {
        addExcerpt(excerpt, question.coveragePct, 1);
    }

    const out = [];
    for (const entry of byReport.values()) {
        const score = singleAr
            ? Number(question.coveragePct ?? entry.maxPct ?? 0)
            : (entry.weightSum > 0 ? entry.weightedPct / entry.weightSum : Number(entry.maxPct ?? 0));
        const fit = answerFitFromScore(score);
        out.push({
            reportId: entry.reportId,
            reportTitle: entry.reportTitle,
            answerExcerpt: entry.texts[0] || '',
            answerText: shortenToSentences(entry.texts.join(' '), 3),
            qualityScore: Number.isFinite(score) ? Math.round(score) : 0,
            fit,
            fitLabel: answerFitLabel(fit),
            linked: Boolean(entry.linked),
        });
    }
    return out.sort(sortFintels);
}

function fintelRefToRow(raw, defaultLinked, linkedIds) {
    const reportId = raw?.reportId;
    if (!reportId) return null;
    const key = String(reportId);
    const linked = raw.linked === true || defaultLinked || linkedIds.has(key);
    const relevance = Number(raw.relevancePct);
    return {
        reportId: key,
        reportTitle: raw.reportTitle || raw.title || key,
        answerExcerpt: '',
        answerText: '',
        qualityScore: Number.isFinite(relevance) ? Math.round(relevance) : 0,
        fit: 'none',
        fitLabel: null,
        linked,
        eeiId: raw.eeiId || null,
        eeiNumber: raw.eeiNumber || null,
    };
}

function snapshotFintelRows(snapshot, linkedIds) {
    const nest = snapshot?.analyticCoverage;
    const linked = Array.isArray(nest?.linkedFintels)
        ? nest.linkedFintels
        : (Array.isArray(snapshot?.linkedFintels) ? snapshot.linkedFintels : []);
    const suggested = Array.isArray(nest?.suggestedFintels)
        ? nest.suggestedFintels
        : (Array.isArray(snapshot?.suggestedFintels)
            ? snapshot.suggestedFintels
            : (Array.isArray(snapshot?.discoveredFintels) ? snapshot.discoveredFintels : []));
    const rows = [];
    for (const raw of linked) {
        const row = fintelRefToRow(raw, true, linkedIds);
        if (row) rows.push(row);
    }
    for (const raw of suggested) {
        const row = fintelRefToRow(raw, false, linkedIds);
        if (row) rows.push(row);
    }
    return rows;
}

function isIcrLevelFintel(fintel) {
    return !fintel?.eeiId && !fintel?.eeiNumber;
}

function fintelMatchesEei(fintel, eei) {
    if (!eei) return false;
    const keys = [eei.id, eei.eeiNumber].filter(Boolean).map(String);
    return keys.includes(String(fintel.eeiId || ''))
        || keys.includes(String(fintel.eeiNumber || ''));
}

function mergeFintels(fromExcerpts, fromSnapshot) {
    const byId = new Map();
    for (const fintel of fromSnapshot || []) {
        if (fintel?.reportId) byId.set(String(fintel.reportId), fintel);
    }
    for (const fintel of fromExcerpts || []) {
        if (!fintel?.reportId) continue;
        const key = String(fintel.reportId);
        const prev = byId.get(key);
        byId.set(key, prev
            ? {
                ...prev,
                ...fintel,
                linked: Boolean(prev.linked || fintel.linked),
                reportTitle: fintel.reportTitle || prev.reportTitle,
            }
            : fintel);
    }
    return [...byId.values()].sort(sortFintels);
}

function linkedIdSet(snapshot) {
    const ids = snapshot?.analyticCoverage?.approvedFintelIds || snapshot?.approvedFintelIds || [];
    return new Set(ids.map(String).filter(Boolean));
}

function asCount(value) {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function matchEeiQuestion(eeiQuestions, eei, index) {
    const byId = eeiQuestions.find((q) => q.eeiId && eei.id && String(q.eeiId) === String(eei.id));
    if (byId) return byId;
    const byNumber = eeiQuestions.find(
        (q) => q.eeiNumber && eei.eeiNumber && String(q.eeiNumber) === String(eei.eeiNumber),
    );
    if (byNumber) return byNumber;
    const needle = normText(eei.text);
    if (needle) {
        const byText = eeiQuestions.find((q) => normText(q.text) === needle);
        if (byText) return byText;
    }
    // Positional fallback: C1 emits EEI questions in EEI order.
    return eeiQuestions[index] || null;
}

function questionView(question, linkedIds) {
    const coveragePct = question?.coveragePct != null ? Number(question.coveragePct) : null;
    const ars = Array.isArray(question?.ars)
        ? question.ars.map(normalizeArView).filter(Boolean)
        : [];
    const status = resolveRequirementCoverageStatus(ars, coveragePct);
    return {
        coveragePct: Number.isFinite(coveragePct) ? coveragePct : null,
        status,
        explanation: question?.explanation || '',
        fintels: fintelsFromC1Question(question, linkedIds),
        ars,
        text: question?.text || '',
        requirementId: question?.id || null,
    };
}

/**
 * Per-EEI coverage + answering FINTELs, from the C1 analytic snapshot
 * (`questions[]`: 1 ICR question + 1 per EEI, each with ARs and excerpts)
 * plus snapshot-level linked / suggested FINTEL refs.
 */
export function buildPirDetailCoverage(pir, snapshot) {
    const eeis = resolvePirEeis(pir);
    const nest = snapshot?.analyticCoverage;
    const questions = (Array.isArray(nest?.questions) ? nest.questions : null)
        || snapshot?.questions
        || [];
    const icrQuestion = questions.find((q) => String(q?.source || '').toUpperCase() === 'ICR') || null;
    const eeiQuestions = questions.filter((q) => String(q?.source || '').toUpperCase() === 'EEI');
    const linkedIds = linkedIdSet(snapshot);
    const snapshotFintels = snapshotFintelRows(snapshot, linkedIds);

    const question = questionView(icrQuestion, linkedIds);
    question.fintels = mergeFintels(
        question.fintels,
        snapshotFintels.filter(isIcrLevelFintel),
    );

    const eeiRows = eeis.map((eei, index) => {
        const matched = matchEeiQuestion(eeiQuestions, eei, index);
        const view = questionView(matched, linkedIds);
        return {
            eei,
            ...view,
            fintels: mergeFintels(
                view.fintels,
                snapshotFintels.filter((fintel) => fintelMatchesEei(fintel, eei)),
            ),
            requirementId: matched?.id || view.requirementId || null,
        };
    });

    const overallCoveragePct = resolveAnalyticHeadlinePct(snapshot);

    return {
        eeis: eeiRows,
        question,
        overallCoveragePct,
        /** ICR-level 1-phrase narrative of covered / missing analytics. */
        summarySentence: toCoverageRationalePhrase(
            nest?.summarySentence || snapshot?.summarySentence || '',
        ),
        /** ICR-level badge from question + EEI statuses (Coverage tab). */
        icrStatus: icrCoverageStatusFromChildren([
            question.status,
            ...eeiRows.map((row) => row.status),
        ]),
        linkedFintelCount: asCount(
            nest?.linkedFintelCount
            ?? snapshot?.linkedFintelCount
            ?? snapshot?.linked_fintel_count,
        ) ?? (Array.isArray(nest?.linkedFintels)
            ? nest.linkedFintels.length
            : (Array.isArray(snapshot?.linkedFintels) ? snapshot.linkedFintels.length : null)),
        discoveredFintelCount: asCount(
            nest?.discoveredFintelCount
            ?? snapshot?.discoveredFintelCount
            ?? snapshot?.discovered_fintel_count
            ?? snapshot?.discoveredCount,
        ) ?? (Array.isArray(nest?.suggestedFintels)
            ? nest.suggestedFintels.length
            : (Array.isArray(snapshot?.suggestedFintels) ? snapshot.suggestedFintels.length : null)),
    };
}
